import { useEffect, useRef, useCallback } from "react"
import socketService from "../services/socketService"

/**
 * Browser-level activity detection for the AFK idle policy.
 *
 * Before this existed the server only learned you were present when you did
 * something it could see — sent a message, started typing, opened a cam. Someone
 * silently watching a webcam or reading the room aged to "away" on the presence
 * dot while fully present, and acting on that signal (the idle kick) would
 * disconnect real users. This hook supplies the missing signal.
 *
 * Design notes:
 * - `mousemove` fires 60+/s, so the listeners do exactly one thing: write a ref.
 *   Activity NEVER enters React state, so this causes zero re-renders.
 * - A single interval decides whether to emit, so continuous mouse movement
 *   costs at most one emit per PING_INTERVAL_MS and a genuinely idle tab emits
 *   nothing at all.
 * - A leading edge makes "I'm back" immediate rather than up to a minute late.
 *   While a warning is on screen the leading edge tightens so any twitch clears
 *   the banner in about one round-trip.
 * - A hidden/background tab is deliberately NOT special-cased. It receives no
 *   input events, so it ages into idle on its own — which is correct, because a
 *   backgrounded tab still pushing a webcam is exactly the case being fixed. We
 *   also never send a keep-alive while hidden, which would defeat the feature.
 */

// How often the pump may emit during sustained activity.
const PING_INTERVAL_MS = 60 * 1000
// Minimum gap between leading-edge emits in the normal case.
const LEADING_EDGE_MS = 60 * 1000
// Tighter gap while a warning is showing, so a twitch clears it promptly.
const LEADING_EDGE_WARNING_MS = 5 * 1000

// Passive, capture-phase so a child that stops propagation can't blind us.
const EVENTS = [
  "pointerdown", // mouse + touch + pen in one
  "keydown", // typing anywhere, including IM windows
  "wheel", // scrolling a chat log without clicking
  "touchstart", // mobile, where pointerdown coverage varies
  "mousemove", // the broad net: the "silently watching cams" case
  "focusin", // keyboard/screen-reader navigation that emits no keydown
]

/**
 * @param {boolean} enabled - run only while connected and the tenant opted in
 * @param {boolean} warningActive - a warning banner is currently showing
 * @returns {{ pingNow: () => void }} - for the "I'm still here" button
 */
export default function useActivityPing(enabled, warningActive) {
  const lastLocalRef = useRef(0)
  const lastSentRef = useRef(0)
  const warnRef = useRef(false)

  useEffect(() => {
    warnRef.current = !!warningActive
  }, [warningActive])

  // Emit unconditionally, bypassing both the throttle and the leading edge.
  // Used by the affirmation button, where a dropped ping means a wrong kick.
  const pingNow = useCallback(() => {
    if (!socketService.isConnected) return
    lastSentRef.current = Date.now()
    lastLocalRef.current = Date.now()
    socketService.sendActivityPing()
  }, [])

  useEffect(() => {
    if (!enabled) return undefined

    const onActivity = () => {
      const now = Date.now()
      lastLocalRef.current = now
      // Leading edge: emit right away if it has been long enough since the last
      // one. Tightened while warned so the banner clears on the first twitch.
      const gap = warnRef.current ? LEADING_EDGE_WARNING_MS : LEADING_EDGE_MS
      if (now - lastSentRef.current >= gap) {
        if (!socketService.isConnected) return
        lastSentRef.current = now
        socketService.sendActivityPing()
      }
    }

    // Returning to the tab is itself a presence signal.
    const onVisibility = () => {
      if (document.visibilityState === "visible") onActivity()
    }

    for (const ev of EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true, capture: true })
    }
    // scroll doesn't bubble from elements, so listen on document in capture.
    document.addEventListener("scroll", onActivity, { passive: true, capture: true })
    document.addEventListener("visibilitychange", onVisibility)

    // The pump: emit only when there was new local activity since the last send.
    const id = setInterval(() => {
      if (!socketService.isConnected) return
      if (lastLocalRef.current <= lastSentRef.current) return // genuinely idle
      lastSentRef.current = Date.now()
      socketService.sendActivityPing()
    }, PING_INTERVAL_MS)

    return () => {
      for (const ev of EVENTS) {
        window.removeEventListener(ev, onActivity, { capture: true })
      }
      document.removeEventListener("scroll", onActivity, { capture: true })
      document.removeEventListener("visibilitychange", onVisibility)
      clearInterval(id)
    }
  }, [enabled])

  return { pingNow }
}
